import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "antd";

import CLIENT from "../../../../lib/api/client";
import ENDPOINTS from "../../../../lib/api/endpoints";
import { AUTH_ROUTES } from "../../authFlow";
import { hydrateProfile } from "../../completeAuth";
import { clearSignupSession, readSignupSession } from "../../authSession";
import { addressFromCoords, addressFromGeocode } from "../../authMappers";
import CONSUMER_SERVICES from "../../../profile/consumerService";
import { draftToProfile, toSavePayload } from "../../../profile/consumerMappers";
import { setProfile } from "../../../profile/profileStore";
import { toUserProfile } from "../../../profile/consumerMappers";
import { showInfoToast } from "../../../../components/Toast";
import {
  ACCEPT_ATTRIBUTE,
  uploadErrorMessage,
  uploadImage,
} from "../../../profile/uploads";

import "../styles.css";

/**
 * The last signup step, and the one that actually registers the consumer.
 *
 * Tokens already exist by the time this mounts, so the screen is
 * authenticated. The draft collected back on SignUp is spent here.
 */
const AddPhoto = () => {
  const navigate = useNavigate();

  const session = useMemo(() => readSignupSession(), []);
  const draft = session?.profileDraft ?? null;

  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const fileInput = useRef(null);

  // Tokens are stored; read both records so the app has a profile to render.
  useEffect(() => {
    hydrateProfile();
  }, []);

  useEffect(() => {
    if (!draft) navigate(AUTH_ROUTES.home, { replace: true });
  }, [draft, navigate]);

  useEffect(() => {
    if (!file) return undefined;

    const url = URL.createObjectURL(file);
    setPreview(url);

    return () => URL.revokeObjectURL(url);
  }, [file]);

  /**
   * Try the server geocoder, fall back to the coordinates we already hold.
   *
   * The response MUST go through `addressFromGeocode`. It is an envelope —
   * `{ address, state, city }` — and this used to forward it whole as the
   * save payload's `address`, which is why `/consumers/save/` answered 400
   * "Malformed JSON request" on web while the app, which maps it, was fine.
   */
  const resolveAddress = async () => {
    const coords = draft.addressCoords;

    if (coords) {
      try {
        const { data } = await CLIENT.get(ENDPOINTS.ADDRESS.fromLocation, {
          params: { latitude: coords.latitude, longitude: coords.longitude },
        });

        const mapped = addressFromGeocode(data, draft.addressLabel);
        if (mapped) return mapped;
      } catch {
        /* fall through to the client-side builder */
      }
    }

    return addressFromCoords(draft.addressLabel, coords, draft.countryCode);
  };

  /** Skip is the same code path with a null image — not a separate branch. */
  const finish = async (withPhoto) => {
    setSaving(true);
    setError("");

    let profileImage = null;

    if (withPhoto && file) {
      try {
        profileImage = await uploadImage(file);
      } catch (caught) {
        // Non-fatal by design: signup must not dead-end on an optional photo.
        showInfoToast(uploadErrorMessage(caught), "Photo not uploaded");
      }
    }

    try {
      const address = await resolveAddress();

      const payload = toSavePayload(draftToProfile(draft), {
        profileImage,
        address,
      });

      const { data } = await CONSUMER_SERVICES.save(payload);

      setProfile(data ? toUserProfile(data) : draftToProfile(draft));
      clearSignupSession();

      navigate(AUTH_ROUTES.home, { replace: true });
    } catch (caught) {
      // The draft stays in storage so the save can be retried rather than
      // leaving a usable account with an empty profile.
      setError(
        caught?.response?.data?.reason ||
          "Could not save your profile. Please try again."
      );
    } finally {
      setSaving(false);
    }
  };

  if (!draft) return null;

  return (
    <div className="auth-page">
      <div className="auth-screen">
        <h1>Add a Photo</h1>
        <p className="auth-sub">
          Add a profile photo so people can recognize you!
        </p>

        <div className="auth-avatar">
          {preview ? (
            <img src={preview} alt="Selected profile" />
          ) : (
            <span className="auth-avatar-empty" aria-hidden="true">
              {draft.firstName?.[0]?.toUpperCase() || "?"}
            </span>
          )}
        </div>

        <input
          ref={fileInput}
          type="file"
          accept={ACCEPT_ATTRIBUTE}
          hidden
          onChange={(event) => setFile(event.target.files?.[0] ?? null)}
        />

        <div className="auth-actions">
          <Button
            className="auth-btn-ghost"
            size="large"
            block
            onClick={() => fileInput.current?.click()}
          >
            {file ? "Choose a different photo" : "Choose a photo"}
          </Button>
        </div>

        <p className="auth-error" aria-live="polite">
          {error}
        </p>

        <div className="auth-actions">
          <Button
            className="auth-btn-primary"
            size="large"
            block
            loading={saving}
            onClick={() => finish(true)}
          >
            Continue
          </Button>

          <Button
            className="auth-btn-text"
            size="large"
            block
            disabled={saving}
            onClick={() => finish(false)}
          >
            Skip for later
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AddPhoto;
